import { Box, Typography } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { AnalyticsOutletContext } from '../../AnalyticsShell';
import { AnalyticsEmpty, AnalyticsError, AnalyticsLoading } from '../../components/AnalyticsState';
import AgentSpeedRanking from './components/AgentSpeedRanking';
import CompletionModeBreakdown from './components/CompletionModeBreakdown';
import OverviewCards from './components/OverviewCards';
import StageTimingComparison from './components/StageTimingComparison';

import { getAssessmentCompletionTimes } from 'api/rest/analytics';
import type { AssessmentCohort, AssessmentCompletionTimesData } from 'hooks/analytics';
import useTranslate from 'hooks/useTranslate';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import useAssessmentCompletionFilters from './useAssessmentCompletionFilters';

const AssessmentCompletionView: FC = () => {
    const { from, to, hasValidDateRange, isSuperSuperAdmin, serviceProviderId } =
        useOutletContext<AnalyticsOutletContext>();
    const { translate } = useTranslate();
    const { agentId, setAgentId } = useAssessmentCompletionFilters();
    const [selectedCohort, setSelectedCohort] = useState<AssessmentCohort>('combined');
    const scopeReady = !isSuperSuperAdmin || Boolean(serviceProviderId);
    const enabled = Boolean(hasValidDateRange && from && to && scopeReady);

    const { data, loading, error } = useAnalyticsData<AssessmentCompletionTimesData>(
        () => {
            if (!from || !to) {
                return Promise.reject(new Error('Assessment analytics date range is required'));
            }
            return getAssessmentCompletionTimes({
                from,
                to,
                agentId,
                serviceProviderId: isSuperSuperAdmin ? serviceProviderId : undefined,
            });
        },
        [from, to, agentId, serviceProviderId, isSuperSuperAdmin],
        enabled
    );

    const agents = data?.agentRanking.agents ?? [];
    const selectedAgent = agents.find((agent) => agent.agentId === agentId) ?? null;
    const selectedAgentName =
        selectedAgent?.agentName ??
        (agentId != null ? `${translate('assessmentAgent')} #${agentId}` : undefined);

    return (
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            {!hasValidDateRange || !from || !to ? (
                <AnalyticsEmpty
                    title={translate('assessmentDateRangeRequired')}
                    description={translate('assessmentDateRangeRequiredDescription')}
                />
            ) : !scopeReady ? (
                <AnalyticsEmpty
                    title={translate('assessmentProviderRequired')}
                    description={translate('assessmentProviderRequiredDescription')}
                />
            ) : loading ? (
                <AnalyticsLoading />
            ) : error ? (
                <AnalyticsError
                    title={translate('assessmentLoadError')}
                    message={translate('assessmentLoadErrorDescription')}
                />
            ) : !data || data.overview.combined.sampleSize === 0 ? (
                <AnalyticsEmpty
                    title={translate('assessmentNoData')}
                    description={translate('assessmentNoDataDescription')}
                />
            ) : (
                <>
                    <OverviewCards
                        overview={data.overview}
                        selectedAgent={
                            selectedAgentName
                                ? {
                                      name: selectedAgentName,
                                      stats: data.selection.total,
                                  }
                                : undefined
                        }
                    />
                    <AgentSpeedRanking
                        agentRanking={data.agentRanking}
                        selectedAgentId={agentId}
                        onAgentSelect={setAgentId}
                    />
                    <CompletionModeBreakdown breakdown={data.overview.breakdown} />
                    <StageTimingComparison
                        stageTiming={data.stageTiming}
                        selectedSectionTotals={
                            agentId != null ? data.selection.sectionTotals : undefined
                        }
                        agents={agents}
                        selectedAgentId={agentId}
                        onAgentSelect={setAgentId}
                        selectedCohort={selectedCohort}
                        onCohortSelect={setSelectedCohort}
                        overallStats={
                            agentId != null
                                ? data.selection.total
                                : (data.overview[selectedCohort] ?? data.overview.combined)
                        }
                    />
                    <Typography component="p" color="text.secondary" sx={{ fontSize: 12, px: 0.5 }}>
                        {translate('assessmentActiveTimeFootnote')}
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default AssessmentCompletionView;
